import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

const PricingDiscussion = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPricingDiscussion();
  }, []);

  const fetchPricingDiscussion = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/docs/pricing-discussion`);
      if (!response.ok) {
        throw new Error('Failed to fetch pricing discussion');
      }
      const data = await response.json();
      setContent(data.content);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <div className="prose prose-slate max-w-none">
            <ReactMarkdown
              components={{
                h1: ({node, ...props}) => <h1 className="text-4xl font-bold text-gray-900 mb-6 border-b pb-3" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-2xl font-semibold text-gray-700 mt-6 mb-3" {...props} />,
                h4: ({node, ...props}) => <h4 className="text-xl font-semibold text-gray-700 mt-4 mb-2" {...props} />,
                p: ({node, ...props}) => <p className="text-gray-700 mb-4 leading-relaxed" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700" {...props} />,
                li: ({node, ...props}) => <li className="ml-4 text-gray-700" {...props} />,
                code: ({node, inline, ...props}) => 
                  inline ? (
                    <code className="bg-gray-100 text-red-600 px-1.5 py-0.5 rounded text-sm font-mono" {...props} />
                  ) : (
                    <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono mb-4" {...props} />
                  ),
                pre: ({node, ...props}) => <pre className="mb-4" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-[#2b3380] pl-4 italic my-4 text-gray-600" {...props} />,
                hr: ({node, ...props}) => <hr className="my-8 border-t-2 border-gray-300" {...props} />,
                a: ({node, ...props}) => <a className="text-[#2b3380] hover:text-[#2b3380] underline" {...props} />,
                table: ({node, ...props}) => <table className="min-w-full border border-gray-300 my-4" {...props} />,
                th: ({node, ...props}) => <th className="border border-gray-300 px-4 py-2 bg-gray-100 font-semibold" {...props} />,
                td: ({node, ...props}) => <td className="border border-gray-300 px-4 py-2" {...props} />,
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingDiscussion;
